import React, { useMemo, useState } from "react";
import { X, Trash2, Download, GitCompare, ChevronRight, Loader2 } from "lucide-react";
import { L, tr } from "@/lib/i18n";
import { useShortlist, removeFromShortlist, clearShortlist } from "@/lib/shortlist";
import { statusPill } from "./statusUtil";
import { visibleUnitFields, formatFieldValue, fieldLabel } from "@/lib/unitFinder";

/**
 * ShortlistPanel — Phase A · Feature 2.
 *
 * Shows units the visitor has marked during a session, with one-tap
 * jumps into Unit Detail, Compare, and PDF bulk export. Lives in the
 * same viewer-shell glass aesthetic as UnitsList so it doesn't feel
 * like a separate app stitched on.
 */
export default function ShortlistPanel({
  slug, inventory, project,
  onClose, onPickUnit, onCompare, onExportPdf,
  lang, defaultLang, fieldDefinitions = [],
}) {
  const codes = useShortlist(slug);
  const blockById = useMemo(() => Object.fromEntries(inventory.groups.map((g) => [g.id, g])), [inventory.groups]);
  const etapById = useMemo(() => Object.fromEntries(
    inventory.groups.filter((g) => g.group_type === "etap").map((g) => [g.id, g])), [inventory.groups]);

  const units = codes
    .map((c) => inventory.entities.find((e) => e.code === c))
    .filter(Boolean);
  const missing = codes.length - units.length;

  const [pdfBusy, setPdfBusy] = useState(false);
  const onPdf = async () => {
    setPdfBusy(true);
    try {
      await onExportPdf(units.map((u) => u.code), "shortlist");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 z-40 viewer-shell fade-in flex flex-col" data-testid="shortlist-panel">
      <div className="px-6 md:px-10 pt-6 pb-4 flex flex-wrap gap-3 items-center justify-between border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">{tr(project, "name", lang, defaultLang) || project.name}</div>
          <div className="font-display text-3xl text-white tracking-tight mt-1">{L(lang, "shortlist")} · {units.length}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {units.length >= 2 && (
            <button
              onClick={() => onCompare(units.map((u) => u.code))}
              className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2"
              data-testid="shortlist-compare-btn"
            >
              <GitCompare className="h-4 w-4" /> {L(lang, "compare")}
            </button>
          )}
          {units.length > 0 && (
            <button
              onClick={onPdf}
              disabled={pdfBusy}
              className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="shortlist-pdf-btn"
            >
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {L(lang, "download_pdf")}
            </button>
          )}
          {units.length > 0 && (
            <button
              onClick={() => { if (confirm(L(lang, "clear_confirm"))) clearShortlist(slug); }}
              className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2"
              data-testid="shortlist-clear-btn"
            >
              <Trash2 className="h-4 w-4" /> {L(lang, "clear")}
            </button>
          )}
          <button
            onClick={onClose}
            className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2"
            data-testid="shortlist-close"
          >
            <X className="h-4 w-4" /> {L(lang, "back_to_tour")}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin px-6 md:px-10 py-6">
        {units.length === 0 && (
          <div className="text-center py-24" data-testid="shortlist-empty">
            <div className="text-white/50 text-sm">{L(lang, "shortlist_empty")}</div>
            <div className="text-white/30 text-xs mt-2">{L(lang, "shortlist_empty_hint")}</div>
          </div>
        )}
        {missing > 0 && (
          <div className="text-xs text-amber-300/80 mb-3" data-testid="shortlist-missing">
            {missing} {L(lang, "unit_unavailable")}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {units.map((u) => {
            const block = blockById[u.group_id];
            const etap = block && etapById[block.parent_group_id];
            const path = [
              etap && (tr(etap, "name", lang, defaultLang) || etap.name),
              block && (tr(block, "name", lang, defaultLang) || block.name),
            ].filter(Boolean).join(" › ");
            return (
              <div
                key={u.id}
                className="rounded-xl glass-light overflow-hidden flex flex-col group"
                data-testid={`shortlist-card-${u.id}`}
              >
                {u.plan_url && (
                  <button
                    onClick={() => onPickUnit(u.id)}
                    className="block w-full aspect-video bg-black/40 overflow-hidden"
                  >
                    <img src={u.plan_preview_url || u.plan_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition" />
                  </button>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  {path && <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5 truncate">{path}</div>}
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-xl text-white tracking-tight">{u.code}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${statusPill(u.status)}`}>{L(lang, u.status)}</span>
                  </div>
                  <div className="text-[11px] text-white/60 mt-1">{L(lang, tr(u, "type", lang, defaultLang) || u.type) || tr(u, "type", lang, defaultLang) || u.type}{u.floor != null && <span className="text-white/40"> · floor {u.floor}</span>}</div>
                  <div className="mt-3 grid grid-cols-2 gap-y-1 text-[11px] text-white/70 flex-1">
                    {visibleUnitFields(u, fieldDefinitions, "card").slice(0, 4).map(({ key: k, fd }) => {
                      const tv = formatFieldValue(u, fd, lang, defaultLang, project);
                      return (
                        <React.Fragment key={k}>
                          <div className="text-white/40 truncate">{fieldLabel(fd, lang, defaultLang)}</div><div className="truncate">{String(tv ?? "")}</div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                    <button
                      onClick={() => removeFromShortlist(slug, u.code)}
                      className="text-[10px] uppercase tracking-wider text-white/50 hover:text-rose-300 inline-flex items-center gap-1"
                      data-testid={`shortlist-remove-${u.code}`}
                    >
                      <Trash2 className="h-3 w-3" /> {L(lang, "remove")}
                    </button>
                    <button
                      onClick={() => onPickUnit(u.id)}
                      className="text-[10px] uppercase tracking-wider text-white/70 hover:text-white inline-flex items-center gap-1"
                      data-testid={`shortlist-open-${u.code}`}
                    >
                      {L(lang, "open")} <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
